import { useEffect, useMemo, useState } from 'react';
import { Box, chakra, Flex, Grid, Heading, Text, Textarea } from '@chakra-ui/react';
import type {
  SiteContent,
  ServiceCard,
  PricingTier,
  ProcessStep,
  ExpectCard,
} from '@denise-augusta/domain';
import { executeAction } from '@/lattice/runtime';
import { usePortalUser } from '@/auth/roles';
import { AiField } from '@/components/portal/AiField';
import { RichTextField } from '@/components/portal/RichTextField';
import { StringListEditor } from '@/components/portal/StringListEditor';
import { isRichTextEmpty } from '@/lib/rich-text';
import { IconSelect } from '@/components/portal/IconSelect';

const SECTIONS = ['Home', 'Services', 'Pricing', 'About', 'Contact', 'Footer', 'Advanced'] as const;
type Section = (typeof SECTIONS)[number];

/** The content slice each tab edits — used to mark tabs containing changes. */
const SECTION_SLICES: Partial<Record<Section, (c: SiteContent) => unknown>> = {
  Home: (c) => [c.hero, c.marquee, c.intro],
  Services: (c) => [c.servicesNote, c.services, c.process],
  Pricing: (c) => [c.tiers, c.addOn, c.tierNote, c.expect, c.contractBasics],
  About: (c) => c.about,
  Contact: (c) => c.contact,
  Footer: (c) => c.footer,
};

/**
 * Structured, card-based editor for every content area of the public site.
 * Edits are saved to a persisted DRAFT (site-content:saveDraft) that never
 * touches the live site until it is promoted (site-content:promoteDraft).
 */
export function ContentEditor() {
  const user = usePortalUser();
  const [draft, setDraft] = useState<SiteContent | null>(null);
  // Published baseline — fields and tabs that differ from it are marked.
  const [published, setPublished] = useState<SiteContent | null>(null);
  const [hasSavedDraft, setHasSavedDraft] = useState(false);
  const [section, setSection] = useState<Section>('Home');
  const [busy, setBusy] = useState<null | 'save' | 'promote' | 'discard'>(null);
  const [dirty, setDirty] = useState(false);
  const [status, setStatus] = useState<{ kind: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    Promise.all([
      executeAction<{ content: SiteContent; isDraft: boolean }>('site-content:getDraft', {}, user),
      executeAction<SiteContent>('site-content:getContent', {}, user),
    ])
      .then(([draftResult, publishedResult]) => {
        setDraft(draftResult.content);
        setHasSavedDraft(draftResult.isDraft);
        setPublished(publishedResult);
      })
      .catch((err: Error) => setStatus({ kind: 'error', text: err.message }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const changedSections = useMemo(() => {
    const changed = new Set<Section>();
    if (!draft || !published) return changed;
    for (const name of SECTIONS) {
      const slice = SECTION_SLICES[name];
      if (slice && JSON.stringify(slice(draft)) !== JSON.stringify(slice(published))) {
        changed.add(name);
      }
    }
    return changed;
  }, [draft, published]);

  const update = (patch: Partial<SiteContent>) => {
    setDraft((current) => (current ? { ...current, ...patch } : current));
    setDirty(true);
  };

  const saveDraft = async (): Promise<boolean> => {
    if (!draft) return false;
    setBusy('save');
    setStatus(null);
    try {
      const saved = await executeAction<SiteContent>('site-content:saveDraft', { content: draft }, user);
      setDraft(saved);
      setHasSavedDraft(true);
      setDirty(false);
      setStatus({ kind: 'success', text: 'Draft saved — the live site is unchanged.' });
      return true;
    } catch (err) {
      setStatus({ kind: 'error', text: err instanceof Error ? err.message : 'Save failed' });
      return false;
    } finally {
      setBusy(null);
    }
  };

  const promote = async () => {
    if (!draft) return;
    // Unsaved edits are part of the draft being promoted — save them first.
    if (dirty) {
      const saved = await saveDraft();
      if (!saved) return;
    }
    setBusy('promote');
    setStatus(null);
    try {
      const promoted = await executeAction<SiteContent>('site-content:promoteDraft', {}, user);
      setDraft(promoted);
      setPublished(promoted);
      setHasSavedDraft(false);
      setDirty(false);
      setStatus({ kind: 'success', text: `Draft promoted — version ${promoted.version} is live.` });
    } catch (err) {
      setStatus({ kind: 'error', text: err instanceof Error ? err.message : 'Promote failed' });
    } finally {
      setBusy(null);
    }
  };

  const discard = async () => {
    if (!window.confirm('Discard the draft and reload the published content?')) return;
    setBusy('discard');
    setStatus(null);
    try {
      await executeAction('site-content:discardDraft', {}, user);
      const current = await executeAction<SiteContent>('site-content:getContent', {}, user);
      setDraft(current);
      setPublished(current);
      setHasSavedDraft(false);
      setDirty(false);
      setStatus({ kind: 'success', text: 'Draft discarded — showing the published content.' });
    } catch (err) {
      setStatus({ kind: 'error', text: err instanceof Error ? err.message : 'Discard failed' });
    } finally {
      setBusy(null);
    }
  };

  if (!draft) {
    return (
      <Box>
        <Heading fontFamily="heading" fontSize="2.2rem" fontWeight="300" color="brand.ink">
          Site Content
        </Heading>
        {status ? (
          <Text mt="1rem" fontSize="0.85rem" color="brand.terracotta">{status.text}</Text>
        ) : (
          <Text mt="1rem" fontSize="0.85rem" color="ink.soft">Loading…</Text>
        )}
      </Box>
    );
  }

  return (
    <Box>
      <Flex justify="space-between" align="center" mb="1.2rem" wrap="wrap" gap="1rem">
        <Box>
          <Heading fontFamily="heading" fontSize="2.2rem" fontWeight="300" color="brand.ink">
            Site Content
          </Heading>
          <Flex fontSize="0.85rem" color="ink.soft" align="center" gap="0.5rem" wrap="wrap">
            {hasSavedDraft || dirty ? (
              <Box as="span" px="0.6rem" py="0.1rem" borderRadius="12px" bg="#FBEDEB" color="brand.terracotta" fontSize="0.62rem" fontWeight="600" letterSpacing="0.12em" textTransform="uppercase">
                Draft
              </Box>
            ) : (
              <Box as="span" px="0.6rem" py="0.1rem" borderRadius="12px" bg="brand.wash" color="brand.mid" fontSize="0.62rem" fontWeight="600" letterSpacing="0.12em" textTransform="uppercase">
                Published · v{draft.version}
              </Box>
            )}
            {dirty && <Box as="span" color="brand.terracotta">unsaved changes</Box>}
            {!dirty && hasSavedDraft && <Box as="span">saved — not live yet</Box>}
          </Flex>
        </Box>
        <Flex gap="0.6rem" wrap="wrap">
          {(hasSavedDraft || dirty) && (
            <chakra.button
              onClick={() => void discard()}
              disabled={busy !== null}
              bg="transparent"
              color="brand.terracotta"
              border="1px solid"
              borderColor="brand.terracotta"
              fontSize="0.72rem"
              fontWeight="500"
              letterSpacing="0.12em"
              textTransform="uppercase"
              px="1.4rem"
              py="0.8rem"
              cursor="pointer"
              borderRadius="24px"
              opacity={busy ? 0.5 : 1}
              _hover={{ bg: '#FBEDEB' }}
            >
              {busy === 'discard' ? 'Discarding…' : 'Discard Draft'}
            </chakra.button>
          )}
          <chakra.button
            onClick={() => void saveDraft()}
            disabled={busy !== null || !dirty}
            bg="paper.white"
            color="brand.ink"
            border="1px solid"
            borderColor="paper.border"
            fontSize="0.72rem"
            fontWeight="500"
            letterSpacing="0.12em"
            textTransform="uppercase"
            px="1.8rem"
            py="0.8rem"
            cursor="pointer"
            borderRadius="24px"
            opacity={busy !== null || !dirty ? 0.5 : 1}
            _hover={{ borderColor: 'brand.mid', color: 'brand.mid' }}
          >
            {busy === 'save' ? 'Saving…' : 'Save Draft'}
          </chakra.button>
          <chakra.button
            onClick={() => void promote()}
            disabled={busy !== null || (!hasSavedDraft && !dirty)}
            bg="brand.mid"
            color="paper.white"
            fontSize="0.72rem"
            fontWeight="500"
            letterSpacing="0.15em"
            textTransform="uppercase"
            px="2.2rem"
            py="0.85rem"
            border="none"
            cursor="pointer"
            borderRadius="24px"
            opacity={busy !== null || (!hasSavedDraft && !dirty) ? 0.5 : 1}
            _hover={{ bg: 'brand.ink' }}
          >
            {busy === 'promote' ? 'Promoting…' : 'Promote to Live'}
          </chakra.button>
        </Flex>
      </Flex>

      {status && (
        <Box
          mb="1rem"
          px="1.2rem"
          py="0.8rem"
          borderRadius="10px"
          fontSize="0.85rem"
          bg={status.kind === 'success' ? 'brand.wash' : '#FBEDEB'}
          border="1px solid"
          borderColor={status.kind === 'success' ? 'brand.pale' : 'brand.terracotta'}
          color={status.kind === 'success' ? 'brand.ink' : 'brand.terracotta'}
        >
          {status.text}
        </Box>
      )}

      {/* Section tabs */}
      <Flex gap="0.4rem" mb="1.6rem" wrap="wrap">
        {SECTIONS.map((name) => (
          <chakra.button
            key={name}
            onClick={() => setSection(name)}
            position="relative"
            display="inline-flex"
            alignItems="center"
            gap="0.4rem"
            bg={section === name ? 'brand.ink' : 'paper.white'}
            color={section === name ? 'paper.white' : 'ink.body'}
            border="1px solid"
            borderColor={
              section === name ? 'brand.ink' : changedSections.has(name) ? '#8FBF3F' : 'paper.border'
            }
            fontSize="0.72rem"
            fontWeight="500"
            letterSpacing="0.1em"
            textTransform="uppercase"
            px="1.2rem"
            py="0.55rem"
            borderRadius="18px"
            cursor="pointer"
            _hover={section === name ? {} : { borderColor: 'brand.mid', color: 'brand.mid' }}
          >
            {name}
            {changedSections.has(name) && (
              <Box
                as="span"
                title="Contains draft changes"
                w="7px"
                h="7px"
                borderRadius="full"
                bg="brand.lime"
                boxShadow="0 0 0 1px rgba(143,191,63,0.6)"
              />
            )}
          </chakra.button>
        ))}
      </Flex>

      {section === 'Home' && <HomeSection draft={draft} published={published ?? draft} update={update} />}
      {section === 'Services' && <ServicesSection draft={draft} published={published ?? draft} update={update} />}
      {section === 'Pricing' && <PricingSection draft={draft} published={published ?? draft} update={update} />}
      {section === 'About' && <AboutSection draft={draft} published={published ?? draft} update={update} />}
      {section === 'Contact' && <ContactSection draft={draft} published={published ?? draft} update={update} />}
      {section === 'Footer' && <FooterSection draft={draft} published={published ?? draft} update={update} />}
      {section === 'Advanced' && (
        <AdvancedSection
          draft={draft}
          onApply={(next) => {
            setDraft(next);
            setDirty(true);
          }}
        />
      )}
    </Box>
  );
}

interface SectionProps {
  draft: SiteContent;
  /** Published baseline — fields differing from it are highlighted. */
  published: SiteContent;
  update: (patch: Partial<SiteContent>) => void;
}

/* ── Shared editor chrome ─────────────────────────────────────────── */

function EditorCard({
  title,
  children,
  hidden,
  onToggleHidden,
  onRemove,
  onMoveUp,
  onMoveDown,
}: {
  title: string;
  children: React.ReactNode;
  hidden?: boolean;
  onToggleHidden?: () => void;
  onRemove?: () => void;
  /** Omit (or pass undefined at a boundary) to disable that direction. */
  onMoveUp?: () => void;
  onMoveDown?: () => void;
}) {
  return (
    <Box
      bg="paper.white"
      border="1px solid"
      borderColor="paper.border"
      borderRadius="16px"
      p="1.5rem"
      opacity={hidden ? 0.55 : 1}
    >
      <Flex justify="space-between" align="center" mb="1rem" gap="0.8rem" wrap="wrap">
        <Heading as="h4" fontFamily="heading" fontSize="1.15rem" fontWeight="400" color="brand.ink">
          {title}
          {hidden && (
            <Box as="span" ml="0.6rem" fontSize="0.6rem" fontFamily="body" fontWeight="600" letterSpacing="0.12em" textTransform="uppercase" color="brand.terracotta">
              Hidden
            </Box>
          )}
        </Heading>
        <Flex gap="0.5rem">
          {(onMoveUp || onMoveDown) && (
            <>
              <SmallButton onClick={onMoveUp ?? (() => {})} disabled={!onMoveUp}>
                ↑
              </SmallButton>
              <SmallButton onClick={onMoveDown ?? (() => {})} disabled={!onMoveDown}>
                ↓
              </SmallButton>
            </>
          )}
          {onToggleHidden && (
            <SmallButton onClick={onToggleHidden}>{hidden ? 'Show' : 'Hide'}</SmallButton>
          )}
          {onRemove && (
            <SmallButton danger onClick={onRemove}>
              Remove
            </SmallButton>
          )}
        </Flex>
      </Flex>
      <Flex direction="column" gap="0.9rem">
        {children}
      </Flex>
    </Box>
  );
}

function SmallButton({
  children,
  onClick,
  danger = false,
  disabled = false,
}: {
  children: React.ReactNode;
  onClick: () => void;
  danger?: boolean;
  disabled?: boolean;
}) {
  return (
    <chakra.button
      type="button"
      onClick={onClick}
      disabled={disabled}
      bg="transparent"
      border="1px solid"
      borderColor={danger ? 'brand.terracotta' : 'paper.border'}
      color={danger ? 'brand.terracotta' : 'ink.body'}
      fontSize="0.66rem"
      fontWeight="500"
      letterSpacing="0.1em"
      textTransform="uppercase"
      px="0.9rem"
      py="0.35rem"
      borderRadius="14px"
      cursor="pointer"
      opacity={disabled ? 0.35 : 1}
      _disabled={{ cursor: 'not-allowed' }}
      _hover={disabled ? undefined : { bg: danger ? '#FBEDEB' : 'brand.wash' }}
    >
      {children}
    </chakra.button>
  );
}

function AddButton({ children, onClick }: { children: React.ReactNode; onClick: () => void }) {
  return (
    <chakra.button
      type="button"
      onClick={onClick}
      bg="transparent"
      border="1px dashed"
      borderColor="brand.pale"
      color="brand.mid"
      fontSize="0.75rem"
      fontWeight="500"
      letterSpacing="0.08em"
      px="1.4rem"
      py="0.8rem"
      borderRadius="12px"
      cursor="pointer"
      _hover={{ bg: 'brand.wash' }}
    >
      {children}
    </chakra.button>
  );
}

function SectionHeading({ children }: { children: React.ReactNode }) {
  return (
    <Heading as="h3" fontFamily="heading" fontSize="1.5rem" fontWeight="400" color="brand.ink" mt="0.5rem">
      {children}
    </Heading>
  );
}

function replaceAt<T>(list: T[], index: number, item: T): T[] {
  return list.map((existing, i) => (i === index ? item : existing));
}

/** Swaps the item at `index` with its neighbor at `index + delta`. */
function moveItem<T>(list: T[], index: number, delta: 1 | -1): T[] {
  const target = index + delta;
  if (target < 0 || target >= list.length) return list;
  const next = [...list];
  [next[index], next[target]] = [next[target], next[index]];
  return next;
}

/* ── Home ─────────────────────────────────────────────────────────── */

function HomeSection({ draft, published, update }: SectionProps) {
  const hero = draft.hero;
  const pubHero = published.hero;
  return (
    <Flex direction="column" gap="1.2rem">
      <EditorCard title="Hero">
        <Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} gap="0.9rem">
          <AiField label="Eyebrow" value={hero.eyebrow} baseline={pubHero.eyebrow} onChange={(v) => update({ hero: { ...hero, eyebrow: v } })} context="the small uppercase eyebrow line above the hero name" />
          <AiField label="Subtitle" value={hero.subtitle} baseline={pubHero.subtitle} onChange={(v) => update({ hero: { ...hero, subtitle: v } })} context="the hero subtitle" />
          <AiField label="First Name" value={hero.firstName} baseline={pubHero.firstName} onChange={(v) => update({ hero: { ...hero, firstName: v } })} context="a first name" />
          <AiField label="Last Name" value={hero.lastName} baseline={pubHero.lastName} onChange={(v) => update({ hero: { ...hero, lastName: v } })} context="a last name" />
        </Grid>
        <RichTextField label="Tagline" value={hero.tagline} baseline={pubHero.tagline} onChange={(v) => update({ hero: { ...hero, tagline: v } })} context="the hero tagline of an executive-support website" />
      </EditorCard>

      <EditorCard title="Marquee">
        <StringListEditor
          items={draft.marquee}
          baseline={published.marquee}
          onChange={(marquee) => update({ marquee })}
          context="a short service name shown in a scrolling marquee"
          addLabel="+ Add marquee item"
        />
      </EditorCard>

      <SectionHeading>Intro Blocks</SectionHeading>
      {draft.intro.map((block, i) => (
        <EditorCard
          key={i}
          title={block.title || `Block ${i + 1}`}
          onRemove={() => update({ intro: draft.intro.filter((_, x) => x !== i) })}
        >
          <Grid templateColumns={{ base: '1fr', md: '2fr 1fr' }} gap="0.9rem">
            <AiField label="Title" value={block.title} baseline={published.intro[i]?.title ?? ''} onChange={(v) => update({ intro: replaceAt(draft.intro, i, { ...block, title: v }) })} context="a short benefit heading" />
            <IconSelect
              label="Icon"
              value={block.icon}
              baseline={published.intro[i]?.icon}
              onChange={(icon) => update({ intro: replaceAt(draft.intro, i, { ...block, icon }) })}
            />
          </Grid>
          <RichTextField label="Body" value={block.body} baseline={published.intro[i]?.body ?? ''} onChange={(v) => update({ intro: replaceAt(draft.intro, i, { ...block, body: v }) })} context="a benefit description on the home page" />
        </EditorCard>
      ))}
      <AddButton onClick={() => update({ intro: [...draft.intro, { icon: 'clock', title: '', body: '' }] })}>
        + Add intro block
      </AddButton>
    </Flex>
  );
}

/* ── Services ─────────────────────────────────────────────────────── */

function ServicesSection({ draft, published, update }: SectionProps) {
  const setService = (i: number, service: ServiceCard) =>
    update({ services: replaceAt(draft.services, i, service) });

  return (
    <Flex direction="column" gap="1.2rem">
      <EditorCard title="Services Note">
        <RichTextField value={draft.servicesNote} baseline={published.servicesNote} onChange={(servicesNote) => update({ servicesNote })} context="the introductory note above the services grid" />
      </EditorCard>

      <SectionHeading>Service Cards</SectionHeading>
      {draft.services.map((service, i) => {
        const pub = published.services[i];
        return (
        <EditorCard
          key={i}
          title={service.title || `Service ${service.number}`}
          hidden={service.hidden}
          onToggleHidden={() => setService(i, { ...service, hidden: !service.hidden })}
          onRemove={() => update({ services: draft.services.filter((_, x) => x !== i) })}
        >
          <Grid templateColumns={{ base: '1fr', md: '80px 1fr' }} gap="0.9rem">
            <AiField label="No." value={service.number} baseline={pub?.number ?? ''} onChange={(v) => setService(i, { ...service, number: v })} />
            <AiField label="Title" value={service.title} baseline={pub?.title ?? ''} onChange={(v) => setService(i, { ...service, title: v })} context="a service category title" />
          </Grid>
          <RichTextField label="Description" value={service.description} baseline={pub?.description ?? ''} onChange={(v) => setService(i, { ...service, description: v })} context="a service card description" />
          <StringListEditor
            label="Includes"
            items={service.includes}
            baseline={pub?.includes ?? []}
            onChange={(includes) => setService(i, { ...service, includes })}
            context="a bullet item describing what a service includes"
            addLabel="+ Add include"
          />
        </EditorCard>
        );
      })}
      <AddButton
        onClick={() =>
          update({
            services: [
              ...draft.services,
              {
                number: String(draft.services.length + 1).padStart(2, '0'),
                title: '',
                description: '',
                includes: [],
              },
            ],
          })
        }
      >
        + Add service
      </AddButton>

      <SectionHeading>Process Steps</SectionHeading>
      {draft.process.map((step, i) => (
        <EditorCard
          key={i}
          title={step.title || `Step ${step.number}`}
          onRemove={() => update({ process: draft.process.filter((_, x) => x !== i) })}
        >
          <Grid templateColumns={{ base: '1fr', md: '80px 1fr' }} gap="0.9rem">
            <AiField label="No." value={step.number} baseline={published.process[i]?.number ?? ''} onChange={(v) => update({ process: replaceAt(draft.process, i, { ...step, number: v }) })} />
            <AiField label="Title" value={step.title} baseline={published.process[i]?.title ?? ''} onChange={(v) => update({ process: replaceAt(draft.process, i, { ...step, title: v }) })} context="a process step title" />
          </Grid>
          <RichTextField label="Body" value={step.body} baseline={published.process[i]?.body ?? ''} onChange={(v) => update({ process: replaceAt(draft.process, i, { ...step, body: v }) })} context="a process step description" />
        </EditorCard>
      ))}
      <AddButton
        onClick={() =>
          update({
            process: [
              ...draft.process,
              { number: String(draft.process.length + 1).padStart(2, '0'), title: '', body: '' } as ProcessStep,
            ],
          })
        }
      >
        + Add process step
      </AddButton>
    </Flex>
  );
}

/* ── Pricing ──────────────────────────────────────────────────────── */

function PricingSection({ draft, published, update }: SectionProps) {
  const setTier = (i: number, tier: PricingTier) => update({ tiers: replaceAt(draft.tiers, i, tier) });
  const setExpect = (i: number, card: ExpectCard) => update({ expect: replaceAt(draft.expect, i, card) });
  const addOn = draft.addOn;
  const pubAddOn = published.addOn;

  return (
    <Flex direction="column" gap="1.2rem">
      <SectionHeading>Tiers</SectionHeading>
      {draft.tiers.map((tier, i) => {
        const pub = published.tiers[i];
        return (
        <EditorCard
          key={i}
          title={tier.name || `Tier ${i + 1}`}
          hidden={tier.hidden}
          onToggleHidden={() => setTier(i, { ...tier, hidden: !tier.hidden })}
          onRemove={() => update({ tiers: draft.tiers.filter((_, x) => x !== i) })}
          onMoveUp={i > 0 ? () => update({ tiers: moveItem(draft.tiers, i, -1) }) : undefined}
          onMoveDown={i < draft.tiers.length - 1 ? () => update({ tiers: moveItem(draft.tiers, i, 1) }) : undefined}
        >
          <Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} gap="0.9rem">
            <AiField label="Name" value={tier.name} baseline={pub?.name ?? ''} onChange={(v) => setTier(i, { ...tier, name: v })} context="a pricing tier name" />
            <AiField label="Subtitle" value={tier.subtitle} baseline={pub?.subtitle ?? ''} onChange={(v) => setTier(i, { ...tier, subtitle: v })} context="a pricing tier subtitle" />
            <AiField label="Starting At" value={tier.startingAt} baseline={pub?.startingAt ?? ''} onChange={(v) => setTier(i, { ...tier, startingAt: v })} placeholder="$3,500" />
            <AiField label="Period" value={tier.period} baseline={pub?.period ?? ''} onChange={(v) => setTier(i, { ...tier, period: v })} placeholder="/month" />
          </Grid>
          <RichTextField label="Description" value={tier.description} baseline={pub?.description ?? ''} onChange={(v) => setTier(i, { ...tier, description: v })} context="a pricing tier positioning line — bold the leading 'Best for' to match the card design" placeholder="Best for executives who…" />
          <Flex>
            <SmallButton onClick={() => setTier(i, { ...tier, featured: !tier.featured })}>
              {tier.featured ? '★ Featured' : '☆ Not featured'}
            </SmallButton>
          </Flex>
        </EditorCard>
        );
      })}
      <AddButton
        onClick={() =>
          update({
            tiers: [
              ...draft.tiers,
              { name: '', subtitle: '', startingAt: '', period: '/month', description: '', featured: false },
            ],
          })
        }
      >
        + Add tier
      </AddButton>

      <EditorCard title="Add-On">
        <Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} gap="0.9rem">
          <AiField label="Title" value={addOn.title} baseline={pubAddOn.title} onChange={(v) => update({ addOn: { ...addOn, title: v } })} context="an add-on package title" />
          <AiField label="Availability" value={addOn.availability} baseline={pubAddOn.availability} onChange={(v) => update({ addOn: { ...addOn, availability: v } })} />
          <AiField label="Starting At" value={addOn.startingAt} baseline={pubAddOn.startingAt} onChange={(v) => update({ addOn: { ...addOn, startingAt: v } })} />
          <AiField label="Period" value={addOn.period} baseline={pubAddOn.period} onChange={(v) => update({ addOn: { ...addOn, period: v } })} />
        </Grid>
        <RichTextField label="Description" value={addOn.description} baseline={pubAddOn.description} onChange={(v) => update({ addOn: { ...addOn, description: v } })} context="an add-on package description" />
        <RichTextField label="What It Covers" value={addOn.coverage} baseline={pubAddOn.coverage} onChange={(v) => update({ addOn: { ...addOn, coverage: v } })} context="a prose summary of what a personal-support add-on covers" />
        <RichTextField label="Fine Print" value={addOn.finePrint} baseline={pubAddOn.finePrint} onChange={(v) => update({ addOn: { ...addOn, finePrint: v } })} context="fine print for an add-on package" />
      </EditorCard>

      <EditorCard title="Tier Note" hidden={isRichTextEmpty(draft.tierNote)}>
        <RichTextField value={draft.tierNote} baseline={published.tierNote} onChange={(tierNote) => update({ tierNote })} context="the boxed reassurance note between the pricing add-on and the Engagement section" />
      </EditorCard>

      <SectionHeading>What to Expect</SectionHeading>
      {draft.expect.map((card, i) => (
        <EditorCard key={i} title={card.title || `Card ${i + 1}`} onRemove={() => update({ expect: draft.expect.filter((_, x) => x !== i) })}>
          <AiField label="Title" value={card.title} baseline={published.expect[i]?.title ?? ''} onChange={(v) => setExpect(i, { ...card, title: v })} />
          <StringListEditor
            label="Paragraphs"
            items={card.paragraphs}
            baseline={published.expect[i]?.paragraphs ?? []}
            onChange={(paragraphs) => setExpect(i, { ...card, paragraphs })}
            context="a paragraph describing how engagements work"
            rich
            addLabel="+ Add paragraph"
          />
          <StringListEditor
            label="Bullets (optional)"
            items={card.bullets ?? []}
            baseline={published.expect[i]?.bullets ?? []}
            onChange={(bullets) => setExpect(i, { ...card, bullets: bullets.length ? bullets : undefined })}
            context="a bullet about ongoing support"
            addLabel="+ Add bullet"
          />
        </EditorCard>
      ))}
      <AddButton onClick={() => update({ expect: [...draft.expect, { title: '', paragraphs: [] }] })}>
        + Add card
      </AddButton>

      <EditorCard title="Contract Basics">
        <AiField label="Heading" value={draft.contractBasics.heading} baseline={published.contractBasics.heading} onChange={(v) => update({ contractBasics: { ...draft.contractBasics, heading: v } })} />
        <StringListEditor
          label="Paragraphs"
          items={draft.contractBasics.paragraphs}
          baseline={published.contractBasics.paragraphs}
          onChange={(paragraphs) => update({ contractBasics: { ...draft.contractBasics, paragraphs } })}
          context="a sentence about contract terms"
          rich
          addLabel="+ Add paragraph"
        />
      </EditorCard>
    </Flex>
  );
}

/* ── About / Contact / Footer ─────────────────────────────────────── */

function AboutSection({ draft, published, update }: SectionProps) {
  const about = draft.about;
  const pub = published.about;
  return (
    <Flex direction="column" gap="1.2rem">
      <EditorCard title="About">
        <AiField label="Heading" value={about.heading} baseline={pub.heading} onChange={(v) => update({ about: { ...about, heading: v } })} context="the about-page heading" />
        <RichTextField label="Pull Quote" value={about.pullQuote} baseline={pub.pullQuote} onChange={(v) => update({ about: { ...about, pullQuote: v } })} context="a personal pull quote on the about page" />
        <StringListEditor
          label="Paragraphs"
          items={about.paragraphs}
          baseline={pub.paragraphs}
          onChange={(paragraphs) => update({ about: { ...about, paragraphs } })}
          context="a personal biography paragraph"
          rich
          addLabel="+ Add paragraph"
        />
      </EditorCard>
    </Flex>
  );
}

function ContactSection({ draft, published, update }: SectionProps) {
  const contact = draft.contact;
  const pub = published.contact;
  return (
    <Flex direction="column" gap="1.2rem">
      <EditorCard title="Contact">
        <AiField label="Heading" value={contact.heading} baseline={pub.heading} onChange={(v) => update({ contact: { ...contact, heading: v } })} context="the contact-page heading" />
        <AiField label="Quote" value={contact.quote} baseline={pub.quote} onChange={(v) => update({ contact: { ...contact, quote: v } })} context="a short inviting quote on the contact page" />
        <AiField label="Email" value={contact.email} baseline={pub.email} onChange={(v) => update({ contact: { ...contact, email: v } })} />
      </EditorCard>
    </Flex>
  );
}

function FooterSection({ draft, published, update }: SectionProps) {
  const footer = draft.footer;
  const pub = published.footer;
  return (
    <Flex direction="column" gap="1.2rem">
      <EditorCard title="Footer">
        <AiField label="Copyright" value={footer.copyright} baseline={pub.copyright} onChange={(v) => update({ footer: { ...footer, copyright: v } })} />
      </EditorCard>
    </Flex>
  );
}

/* ── Advanced (raw JSON) ──────────────────────────────────────────── */

function AdvancedSection({
  draft,
  onApply,
}: {
  draft: SiteContent;
  onApply: (next: SiteContent) => void;
}) {
  const [text, setText] = useState(() => JSON.stringify(draft, null, 2));
  const [error, setError] = useState<string | null>(null);

  const apply = () => {
    try {
      onApply(JSON.parse(text) as SiteContent);
      setError(null);
    } catch {
      setError('Invalid JSON — fix the syntax before applying.');
    }
  };

  return (
    <Box>
      <Text fontSize="0.85rem" color="ink.soft" mb="0.8rem">
        The full content document. Apply copies it into the draft — Publish (above) makes it live.
      </Text>
      {error && (
        <Text fontSize="0.8rem" color="brand.terracotta" mb="0.6rem">
          {error}
        </Text>
      )}
      <Textarea
        value={text}
        onChange={(event) => setText(event.target.value)}
        fontFamily="mono"
        fontSize="0.78rem"
        minH="55vh"
        bg="paper.white"
        border="1px solid"
        borderColor="paper.border"
        borderRadius="12px"
        p="1.2rem"
        spellCheck={false}
      />
      <Flex justify="flex-end" mt="0.8rem">
        <SmallButton onClick={apply}>Apply to draft</SmallButton>
      </Flex>
    </Box>
  );
}
